import { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RequiredMailSchema, RequiredStringSchema } from "@ecommerce/shared-utils";
import { HttpCache, HttpPaymentOption } from "../../http";
import { HttpVodaActualizacionDatos } from "../../http/httpVodaActualizacionDatos";
import { DOC_DNI_ID } from "../../types/purchaseOrder/Order";
import type { CardTypeViewDTO, OrderPaymentOptionViewDTO } from "../../types/purchaseOrder/Order";
import type { EntityWithIdAndDescription } from "../../types/baseEntities";
import type { AfipResponsableTipoVista } from "../../http/httpCache";
import {
    ACTUALIZACION_DATOS_DEFAULT_VALUES,
    type ActualizacionDatosFormValues,
    type VodaActualizacionDatosPayload,
} from "./ActualizacionDatosTypes";
import { PROVEEDOR_PAGO } from "../../components/Checkout/checkoutConstants";

const domicilioShape = (cpRequerido: boolean) => ({
    calle: RequiredStringSchema.nullable(),
    calleNro: yup.string().nullable(),
    pisoDpto: yup.string().nullable(),
    codProvincia: yup
        .number()
        .typeError("Campo obligatorio")
        .required("Campo obligatorio")
        .positive("Campo obligatorio"),
    descLocalidad: RequiredStringSchema.nullable(),
    codigoPostal: cpRequerido ? RequiredStringSchema : yup.string().nullable(),
    barrioPrivado: yup.boolean().nullable(),
    // .nullable() en la base: nombreBarrio / lote arrancan en null (domicilioVacio) y solo
    // son obligatorios cuando "Barrio Privado" esta tildado. Sin .nullable() yup rechazaba
    // el null y el form quedaba invalido aunque el checkbox estuviera destildado.
    nombreBarrio: yup
        .string()
        .nullable()
        .when("barrioPrivado", {
            is: true,
            then: (rule) => rule.required("Campo obligatorio"),
        }),
    lote: yup
        .string()
        .nullable()
        .when("barrioPrivado", {
            is: true,
            then: (rule) => rule.required("Campo obligatorio"),
        }),
});

const domicilioFacturacionSchema = yup.object().shape(domicilioShape(true));
const domicilioInstalacionSchema = yup.object().shape(domicilioShape(false));

const schema = yup.object().shape({
    datosPersonales: yup.object().shape({
        mail: RequiredMailSchema,
        tinCodDocumentoTipo: yup
            .number()
            .typeError("Campo obligatorio")
            .required("Campo obligatorio"),
        documento: yup.string().when("tinCodDocumentoTipo", {
            is: DOC_DNI_ID,
            then: (rule) => rule.required("Campo obligatorio"),
        }),
        nombre: yup.string().when("tinCodDocumentoTipo", {
            is: DOC_DNI_ID,
            then: (rule) => rule.required("Campo obligatorio"),
        }),
        apellido: yup.string().when("tinCodDocumentoTipo", {
            is: DOC_DNI_ID,
            then: (rule) => rule.required("Campo obligatorio"),
        }),
        vchCuit: yup.string().when("tinCodDocumentoTipo", {
            is: (val: number | null) => val != null && val !== DOC_DNI_ID,
            then: (rule) => rule.required("Campo obligatorio"),
        }),
        vchRazonSocial: yup.string().when("tinCodDocumentoTipo", {
            is: (val: number | null) => val != null && val !== DOC_DNI_ID,
            then: (rule) => rule.required("Campo obligatorio"),
        }),
        codigoArea: RequiredStringSchema,
        telefono: RequiredStringSchema,
    }),
    domicilioFacturacion: domicilioFacturacionSchema,
    mismoDomicilioInstalacion: yup.boolean(),
    domicilioInstalacion: yup.object().when("mismoDomicilioInstalacion", {
        is: false,
        then: () => domicilioInstalacionSchema,
    }),
    idPagoOpcion: RequiredStringSchema,
    // codPagoProveedor viaja dentro de datosPago (lo setea PaymentOptionSection al elegir
    // tarjeta/CBU), asi que la condicion se resuelve dentro del propio objeto sin necesitar
    // contexto externo del resolver.
    datosPago: yup
        .object()
        .nullable()
        .shape({
            codPagoProveedor: yup.number().nullable(),
            tarjetaNombreTitular: yup
                .string()
                .nullable()
                .when("codPagoProveedor", {
                    is: PROVEEDOR_PAGO.TARJETA,
                    then: (rule) => rule.required("Campo obligatorio"),
                }),
            codTarjetaTipo: yup
                .number()
                .nullable()
                .when("codPagoProveedor", {
                    is: PROVEEDOR_PAGO.TARJETA,
                    then: (rule) => rule.required("Campo obligatorio"),
                }),
            tarjetaNumero: yup
                .string()
                .nullable()
                .when("codPagoProveedor", {
                    is: PROVEEDOR_PAGO.TARJETA,
                    then: (rule) => rule.required("Campo obligatorio"),
                }),
            tarjetaFechaVto: yup
                .string()
                .nullable()
                .when("codPagoProveedor", {
                    is: PROVEEDOR_PAGO.TARJETA,
                    then: (rule) => rule.required("Campo obligatorio").min(5, "Formato MM/AA"),
                }),
            cbu: yup
                .string()
                .nullable()
                .when("codPagoProveedor", {
                    is: PROVEEDOR_PAGO.CBU,
                    then: (rule) => rule.required("Campo obligatorio"),
                }),
        }),
});

export interface ActualizacionDatosCatalogos {
    documentoTipos: EntityWithIdAndDescription[];
    afipResponsableTipos: AfipResponsableTipoVista[];
    tarjetaTipos: CardTypeViewDTO[];
    paymentOptions: OrderPaymentOptionViewDTO[];
    provincias: EntityWithIdAndDescription[];
}

export type EnvioEstado = "idle" | "enviando" | "enviado" | "error";

export const useActualizacionDatosForm = () => {
    const methods = useForm<ActualizacionDatosFormValues>({
        defaultValues: ACTUALIZACION_DATOS_DEFAULT_VALUES,
        resolver: yupResolver(schema) as any,
        mode: "onBlur",
    });

    const [catalogos, setCatalogos] = useState<ActualizacionDatosCatalogos>({
        documentoTipos: [],
        afipResponsableTipos: [],
        tarjetaTipos: [],
        paymentOptions: [],
        provincias: [],
    });

    const [estado, setEstado] = useState<EnvioEstado>("idle");
    const [errorMensaje, setErrorMensaje] = useState<string | null>(null);

    useEffect(() => {
        HttpCache.getIdentityDocumentTypes()
            .then((documentoTipos) => {
                setCatalogos((prev) => ({ ...prev, documentoTipos }));
                const actual = methods.getValues("datosPersonales.tinCodDocumentoTipo");
                if (actual == null && documentoTipos.length > 0) {
                    methods.setValue(
                        "datosPersonales.tinCodDocumentoTipo",
                        documentoTipos[0].id as any,
                    );
                }
            })
            .catch(() => {});
        HttpCache.getAfipResponsableTipos()
            .then((afipResponsableTipos) =>
                setCatalogos((prev) => ({ ...prev, afipResponsableTipos })),
            )
            .catch(() => {});
        HttpCache.getTiposTarjeta()
            .then((tarjetaTipos) => setCatalogos((prev) => ({ ...prev, tarjetaTipos })))
            .catch(() => {});
        HttpCache.getProvincies()
            .then((provincias) => setCatalogos((prev) => ({ ...prev, provincias })))
            .catch(() => {});
        HttpPaymentOption.getPaymentOptions<OrderPaymentOptionViewDTO>()
            .then((paymentOptions) => setCatalogos((prev) => ({ ...prev, paymentOptions })))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const idPagoOpcion = methods.watch("idPagoOpcion");
    const selectedPaymentProviderCode = useMemo(() => {
        const opt = catalogos.paymentOptions.find(
            (o: any) => String(o?.id) === String(idPagoOpcion),
        );
        return opt ? Number((opt as any).codPagoProveedor) : null;
    }, [idPagoOpcion, catalogos.paymentOptions]);

    const construirPayload = useCallback(
        (values: ActualizacionDatosFormValues): VodaActualizacionDatosPayload => {
            const documentoTipoDesc =
                catalogos.documentoTipos.find(
                    (t) => t.id === values.datosPersonales.tinCodDocumentoTipo,
                )?.descripcion ?? null;
            const afipResponsableTipoDesc =
                catalogos.afipResponsableTipos.find(
                    (t) => t.id === values.datosPersonales.tinCodAfipResponsableTipo,
                )?.descripcion ?? null;
            const paymentOption = catalogos.paymentOptions.find(
                (o: any) => String(o?.id) === String(values.idPagoOpcion),
            ) as any;
            const tarjetaTipoDesc =
                catalogos.tarjetaTipos.find((t) => t.id === values.datosPago?.codTarjetaTipo)
                    ?.descripcion ?? null;
            const resolverProvincia = (codProvincia: number | null) =>
                catalogos.provincias.find((p) => p.id === codProvincia)?.descripcion ?? null;

            const mapDomicilio = (dom: ActualizacionDatosFormValues["domicilioFacturacion"]) => ({
                calle: dom.calle,
                calleNro: dom.calleNro,
                pisoDpto: dom.pisoDpto,
                codProvincia: dom.codProvincia,
                provincia: resolverProvincia(dom.codProvincia),
                descLocalidad: dom.descLocalidad,
                codigoPostal: dom.codigoPostal,
                barrioPrivado: !!dom.barrioPrivado,
                nombreBarrio: dom.nombreBarrio ?? null,
                lote: dom.lote ?? null,
            });

            return {
                mail: values.datosPersonales.mail,
                tinCodDocumentoTipo: values.datosPersonales.tinCodDocumentoTipo,
                documentoTipoDesc,
                nroDocumento: values.datosPersonales.documento,
                nombre: values.datosPersonales.nombre,
                apellido: values.datosPersonales.apellido,
                cuit: values.datosPersonales.vchCuit,
                razonSocial: values.datosPersonales.vchRazonSocial,
                sinCodAfipResponsableTipo: values.datosPersonales.tinCodAfipResponsableTipo,
                afipResponsableTipoDesc,
                codigoArea: values.datosPersonales.codigoArea,
                telefono: values.datosPersonales.telefono,
                domicilioFacturacion: mapDomicilio(values.domicilioFacturacion),
                mismoDomicilioInstalacion: values.mismoDomicilioInstalacion,
                domicilioInstalacion: values.mismoDomicilioInstalacion
                    ? null
                    : mapDomicilio(values.domicilioInstalacion),
                idPagoOpcion: values.idPagoOpcion,
                formaPagoDesc: paymentOption?.descPagoOpcion ?? null,
                codPagoProveedor: paymentOption ? Number(paymentOption.codPagoProveedor) : null,
                datosTarjeta:
                    selectedPaymentProviderCode === PROVEEDOR_PAGO.TARJETA && values.datosPago
                        ? {
                              nombreTitular: values.datosPago.tarjetaNombreTitular ?? null,
                              codTarjetaTipo: values.datosPago.codTarjetaTipo ?? null,
                              tarjetaTipoDesc,
                              numero: values.datosPago.tarjetaNumero ?? null,
                              fechaVto: values.datosPago.tarjetaFechaVto ?? null,
                          }
                        : null,
                cbu:
                    selectedPaymentProviderCode === PROVEEDOR_PAGO.CBU
                        ? (values.datosPago?.cbu ?? null)
                        : null,
            };
        },
        [catalogos, selectedPaymentProviderCode],
    );

    const onSubmit = methods.handleSubmit(
        async (values) => {
            setEstado("enviando");
            setErrorMensaje(null);
            try {
                await HttpVodaActualizacionDatos.enviar(construirPayload(values));
                setEstado("enviado");
            } catch (error: any) {
                setEstado("error");
                setErrorMensaje(error?.message ?? "No se pudo enviar la actualizacion.");
            }
        },
        () => {
            // Errores de validacion: react-hook-form ya marca los campos, no hace falta nada mas.
        },
    );

    return {
        methods,
        catalogos,
        estado,
        errorMensaje,
        selectedPaymentProviderCode,
        onSubmit,
    };
};
